import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UsersIcon,
  ClockIcon,
  ServerIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface AnalyticsData {
  overview: {
    totalRevenue: number;
    totalBookings: number;
    avgBookingValue: number;
    monthlyRevenue: number;
    monthlyBookings: number;
    revenueGrowth: string;
    bookingsGrowth: string;
  };
  chartData: Array<{
    month: string;
    revenue: number;
    bookings: number;
  }>;
  topPackages: Array<{
    _id: string;
    name: string;
    totalRevenue: number;
    totalBookings: number;
    avgAmount: number;
    isActive: boolean;
  }>;
  statusData: Array<{
    _id: string;
    count: number;
    totalAmount: number;
  }>;
  systemHealth: {
    packageStats: number;
    resortStats: number;
    recentActivity: Array<any>;
    systemMetrics: {
      uptime: string;
      responseTime: string;
      errorRate: string;
      apiCalls: number;
      activeUsers: number;
      serverLoad: number;
    };
  };
}

const AdminAnalytics: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      const [overview, revenueChart, topPackages, bookingStatus, systemHealth] = await Promise.all([
        axios.get(`${baseURL}/api/analytics/overview`, { headers }),
        axios.get(`${baseURL}/api/analytics/revenue-chart`, { headers }),
        axios.get(`${baseURL}/api/analytics/top-packages`, { headers }),
        axios.get(`${baseURL}/api/analytics/booking-status`, { headers }),
        axios.get(`${baseURL}/api/analytics/system-health`, { headers })
      ]);

      setData({
        overview: overview.data.overview,
        chartData: revenueChart.data.chartData,
        topPackages: topPackages.data.topPackages,
        statusData: bookingStatus.data.statusData,
        systemHealth: systemHealth.data
      });
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getGrowthColor = (growth: string) => {
    return growth.startsWith('+') ? 'text-green-600' : 'text-red-600';
  };

  const getGrowthIcon = (growth: string) => {
    return growth.startsWith('+') ? ArrowTrendingUpIcon : ArrowTrendingDownIcon;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-medium">{error || 'No data available'}</p>
        <button 
          onClick={fetchAnalyticsData}
          className="mt-4 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Analytics - Bali Getaway Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="text-gray-600">Comprehensive business insights and performance metrics</p>
            </div>
            <button
              onClick={fetchAnalyticsData}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Refresh Data
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-green-500 rounded-lg p-3">
                <CurrencyDollarIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                <div className="flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(data.overview.totalRevenue)}
                  </p>
                </div>
                <div className="flex items-center mt-1">
                  <span className={`text-sm font-medium ${getGrowthColor(data.overview.revenueGrowth)}`}>
                    {data.overview.revenueGrowth}
                  </span>
                  <span className="text-sm text-gray-500 ml-1">this month</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-blue-500 rounded-lg p-3">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Total Bookings</p>
                <div className="flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">{data.overview.totalBookings}</p>
                </div>
                <div className="flex items-center mt-1">
                  <span className={`text-sm font-medium ${getGrowthColor(data.overview.bookingsGrowth)}`}>
                    {data.overview.bookingsGrowth}
                  </span>
                  <span className="text-sm text-gray-500 ml-1">this month</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-purple-500 rounded-lg p-3">
                <ChartBarIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Avg Booking Value</p>
                <div className="flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(data.overview.avgBookingValue)}
                  </p>
                </div>
                <p className="text-sm text-gray-500 mt-1">per booking</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="bg-orange-500 rounded-lg p-3">
                <UsersIcon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-500">Monthly Revenue</p>
                <div className="flex items-baseline">
                  <p className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(data.overview.monthlyRevenue)}
                  </p>
                </div>
                <p className="text-sm text-gray-500 mt-1">{data.overview.monthlyBookings} bookings</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Revenue Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend (Last 12 Months)</h2>
            <div className="space-y-4">
              {data.chartData.slice(-6).map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                    <span className="text-sm font-medium text-gray-700">{item.month}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(item.revenue)}</p>
                    <p className="text-xs text-gray-500">{item.bookings} bookings</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Packages */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Packages</h2>
            <div className="space-y-4">
              {data.topPackages.slice(0, 5).map((pkg, index) => (
                <div key={pkg._id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-3">
                      <span className="text-white font-bold text-sm">#{index + 1}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{pkg.name}</p>
                      <p className="text-xs text-gray-500">{pkg.totalBookings} bookings</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-gray-900">{formatCurrency(pkg.totalRevenue)}</p>
                    <p className="text-xs text-gray-500">{formatCurrency(pkg.avgAmount)} avg</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">System Health & Performance</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* System Metrics */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">System Metrics</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Uptime</span>
                  <span className="text-sm font-semibold text-green-600">{data.systemHealth.systemMetrics.uptime}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Response Time</span>
                  <span className="text-sm font-semibold text-blue-600">{data.systemHealth.systemMetrics.responseTime}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Error Rate</span>
                  <span className="text-sm font-semibold text-yellow-600">{data.systemHealth.systemMetrics.errorRate}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Server Load</span>
                  <span className="text-sm font-semibold text-purple-600">{data.systemHealth.systemMetrics.serverLoad}%</span>
                </div>
              </div>
            </div>

            {/* Content Stats */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Content Statistics</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Active Packages</span>
                  <span className="text-sm font-semibold text-gray-900">{data.systemHealth.packageStats}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Active Resorts</span>
                  <span className="text-sm font-semibold text-gray-900">{data.systemHealth.resortStats}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">API Calls Today</span>
                  <span className="text-sm font-semibold text-gray-900">{data.systemHealth.systemMetrics.apiCalls.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Active Users</span>
                  <span className="text-sm font-semibold text-gray-900">{data.systemHealth.systemMetrics.activeUsers}</span>
                </div>
              </div>
            </div>

            {/* Service Status */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Service Status</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm text-gray-600">Database</span>
                  <span className="ml-auto text-sm text-green-600 font-medium">Operational</span>
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm text-gray-600">Payment Gateway</span>
                  <span className="ml-auto text-sm text-green-600 font-medium">Operational</span>
                </div>
                <div className="flex items-center">
                  <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm text-gray-600">Email Service</span>
                  <span className="ml-auto text-sm text-green-600 font-medium">Operational</span>
                </div>
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mr-2" />
                  <span className="text-sm text-gray-600">LeadSquare API</span>
                  <span className="ml-auto text-sm text-yellow-600 font-medium">Monitoring</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Status Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Booking Status Distribution</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.statusData.map((status) => (
              <div key={status._id} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-bold text-gray-900">{status.count}</p>
                <p className="text-sm text-gray-600 capitalize">{status._id.replace('_', ' ')}</p>
                <p className="text-xs text-gray-500">{formatCurrency(status.totalAmount)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminAnalytics;
